import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Wrench, Play, MagnifyingGlass, Warning, CloudArrowDown, ArrowClockwise } from "@phosphor-icons/react";
import { fmtDateTime } from "../lib/format";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const _fmtTime = (iso) => {
  if (!iso) return "\u2014";
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 60000); // minutes ago
    const rel = diff < 1 ? "just now" : diff < 60 ? `${diff}m ago` : diff < 1440 ? `${Math.round(diff / 60)}h ago` : `${Math.round(diff / 1440)}d ago`;
    return `${fmtDateTime(iso)} (${rel})`;
  } catch {
    return iso;
  }
};

// One-off maintenance actions, kept OUT of the Cockpit on purpose so they aren't run by mistake.
// Each action supports a Dry Run (preview, no writes) and a Confirm & Run (applies changes).
const ACTIONS = [
  {
    key: "dedupe",
    title: "Merge duplicate customers + fix name casing",
    endpoint: "/admin/customers/dedupe",
    blurb:
      "Merges customer-master records that collide on a normalized name (e.g. 'Legrand AV Inc.' vs 'LEGRAND AV INC.') into the one with a pcode, then repoints every PO/invoice to the master's exact casing. Fixes the offset/name-match issues for good.",
    testid: "adminx-dedupe",
  },
  {
    key: "legrand",
    title: "Apply Legrand Statistical delivery date as due date",
    endpoint: "/admin/legrand/fix-statistical-due-dates",
    blurb:
      "Re-reads each Legrand PO's stored document and sets the line due date to the per-line Statistical delivery date. For already-pushed POs it also re-pushes so the live ERP (dbo.poi DelivDate + LineUID) stays consistent. Safe to run periodically.",
    testid: "adminx-legrand",
  },
  {
    key: "backfill",
    title: "Fix pushed POs missing from the POs / Open Orders list",
    endpoint: "/admin/intake/backfill-open-orders",
    blurb:
      "Materializes the canonical open-order record in Mongo for intake POs that were pushed to the ERP before this became automatic, so they reappear in POs / Open Orders. Read-only on the ERP; idempotent.",
    testid: "adminx-backfill",
  },
];

const _Stat = ({ label, value, accent }) => (
  <div style={{ minWidth: 128 }}>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 800, color: accent || "#0F172A", marginTop: 2 }}>{value}</div>
  </div>
);

// NEW: live ERP Invoice Sync status + one-click "Run sync now" (no curl / PowerShell / tokens).
const InvoiceSyncPanel = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/replica/erp-invoice-sync/auto-status");
      setStatus(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load sync status (admin only)");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runNow = async () => {
    if (!window.confirm("Run a full ERP invoice sync now?\n\nThis pulls live ERP invoices into the app (read-only on the ERP; idempotent). It can take up to a minute.")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/replica/erp-invoice-sync", { confirm: true });
      const jobId = data.job_id;
      toast.success("Sync started\u2026");
      let done = false, tries = 0;
      while (!done && tries < 120) {
        await sleep(2500); tries++;
        const { data: job } = await api.get(`/replica/erp-invoice-sync/status?job_id=${jobId}`);
        if (job.status === "done") {
          done = true;
          toast.success(`Sync complete \u2014 upserted ${job.result?.upserted ?? 0}, scanned ${job.result?.erp_gst_scanned ?? 0}`);
        } else if (job.status === "error") {
          done = true;
          toast.error(`Sync failed: ${(job.error || "").slice(0, 160)}`);
        }
      }
      if (!done) toast.message("Sync still running \u2014 check status again shortly.");
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed (admin only)");
    } finally {
      setRunning(false);
    }
  };

  const r = status?.last_result || {};
  const enabled = !!status?.enabled;
  const ok = status?.last_status === "ok";

  return (
    <div
      data-testid="adminx-invoice-sync-card"
      style={{
        border: "1px solid #BAE6FD", borderRadius: 12, padding: 18,
        background: "linear-gradient(180deg,#F0F9FF 0%,#FFFFFF 60%)",
        boxShadow: "0 1px 3px rgba(2,8,23,0.06)", marginBottom: 22,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
        <CloudArrowDown size={20} weight="bold" color="#0284C7" />
        <div style={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A" }}>ERP Invoice Sync</div>
        <span
          data-testid="adminx-invoice-sync-new-badge"
          style={{
            fontSize: 10, fontWeight: 900, letterSpacing: 0.6, color: "#fff",
            background: "linear-gradient(90deg,#F97316,#EF4444)", padding: "2px 8px",
            borderRadius: 999, textTransform: "uppercase",
          }}
        >
          New!
        </span>
        <span
          data-testid="adminx-invoice-sync-enabled"
          style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 12, fontWeight: 700, color: enabled ? "#15803D" : "#B91C1C",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 999, background: enabled ? "#22C55E" : "#EF4444", display: "inline-block" }} />
          Auto-poller {enabled ? `on (every ${Math.round((status?.interval_minutes || 0) / 60)}h)` : "OFF"}
        </span>
      </div>
      <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.55, marginBottom: 14 }}>
        Pulls live ERP invoices (dbo.Gst/Gsti) into the app, including transfer-pricing invoices
        (e.g. billed via AVM, shipped to GEOTEK / MacLean-York). Runs automatically; use
        <strong> Run sync now</strong> to force an immediate pull after a redeploy or a data fix.
      </div>

      {loading ? (
        <div style={{ fontSize: 12.5, color: "#64748B" }}>{"Loading status\u2026"}</div>
      ) : (
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", padding: "12px 0", borderTop: "1px solid #E0F2FE", borderBottom: "1px solid #E0F2FE", marginBottom: 14 }}>
          <_Stat label="Last run" value={_fmtTime(status?.last_run_at)} />
          <_Stat label="Status" value={ok ? "OK" : (status?.last_status || "\u2014")} accent={ok ? "#15803D" : "#B45309"} />
          <_Stat label="Upserted" value={r.upserted ?? "\u2014"} />
          <_Stat label="Scanned" value={r.erp_gst_scanned ?? "\u2014"} />
          <_Stat label="Skipped (seed)" value={r.skipped_covered_by_seed ?? "\u2014"} />
          <_Stat label="Customers" value={r.customer_count ?? "\u2014"} />
          <_Stat label="Next run" value={_fmtTime(status?.next_run_at)} />
        </div>
      )}
      {status?.last_error && (
        <div style={{ fontSize: 12, color: "#B91C1C", marginBottom: 12 }}>Last error: {String(status.last_error).slice(0, 200)}</div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button
          className="jde-btn"
          disabled={loading || running}
          onClick={loadStatus}
          data-testid="adminx-invoice-sync-refresh"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <ArrowClockwise size={14} weight="bold" />
          Refresh status
        </button>
        <button
          className="jde-btn jde-btn-primary"
          disabled={running || loading}
          onClick={runNow}
          data-testid="adminx-invoice-sync-run"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <CloudArrowDown size={14} weight="bold" />
          {running ? "Syncing\u2026" : "Run sync now"}
        </button>
      </div>
    </div>
  );
};

const Card = ({ action }) => {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const run = async (confirm) => {
    setBusy(confirm ? "confirm" : "dry");
    setResult(null);
    try {
      const { data } = await api.post(`${action.endpoint}?confirm=${confirm}`, {});
      setResult(data);
      toast.success(
        confirm
          ? `${action.title} — applied`
          : `${action.title} — dry-run preview ready`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed (admin only)");
    } finally {
      setBusy("");
    }
  };

  return (
    <div
      data-testid={`${action.testid}-card`}
      style={{
        border: "1px solid #E2E8F0", borderRadius: 10, padding: 18, background: "#fff",
        boxShadow: "0 1px 2px rgba(2,8,23,0.04)",
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 800, color: "#0F172A", marginBottom: 6 }}>{action.title}</div>
      <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.55, marginBottom: 14 }}>{action.blurb}</div>
      <code style={{ fontSize: 11, color: "#64748B", background: "#F1F5F9", padding: "2px 7px", borderRadius: 4 }}>
        POST {action.endpoint}
      </code>

      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button
          className="jde-btn"
          disabled={!!busy}
          onClick={() => run(false)}
          data-testid={`${action.testid}-dryrun`}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <MagnifyingGlass size={14} weight="bold" />
          {busy === "dry" ? "Running\u2026" : "Dry Run (preview)"}
        </button>
        <button
          className="jde-btn jde-btn-primary"
          disabled={!!busy}
          onClick={() => {
            if (window.confirm(`Run "${action.title}" for real? This writes changes.`)) run(true);
          }}
          data-testid={`${action.testid}-confirm`}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Play size={14} weight="bold" />
          {busy === "confirm" ? "Applying\u2026" : "Confirm & Run"}
        </button>
      </div>

      {result && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 4, color: result.dry_run ? "#B45309" : "#15803D" }}>
            {result.dry_run ? "DRY RUN (no changes written)" : "APPLIED"}
          </div>
          <pre
            data-testid={`${action.testid}-result`}
            style={{
              fontSize: 11, background: "#0F172A", color: "#E2E8F0", padding: 12, borderRadius: 8,
              maxHeight: 320, overflow: "auto", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word",
            }}
          >
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default function AdminX() {
  return (
    <Layout breadcrumb={["AdminX"]}>
      <div data-testid="adminx-page" style={{ maxWidth: 860, margin: "0 auto", padding: "8px 4px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Wrench size={22} weight="bold" color="#0EA5E9" />
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: "#0F172A" }}>AdminX &mdash; One-time maintenance</h1>
        </div>

        <InvoiceSyncPanel />

        <div
          style={{
            display: "flex", gap: 8, alignItems: "flex-start", background: "#FFFBEB",
            border: "1px solid #FCD34D", borderRadius: 8, padding: "10px 12px", marginBottom: 20,
          }}
        >
          <Warning size={16} weight="bold" color="#B45309" style={{ marginTop: 1, flex: "0 0 auto" }} />
          <div style={{ fontSize: 12.5, color: "#92400E", lineHeight: 1.5 }}>
            Run these rarely and intentionally. Always <strong>Dry Run</strong> first to preview, then
            <strong> Confirm &amp; Run</strong>. Confirmed actions write to Mongo and, where noted, to the live ERP.
          </div>
        </div>

        <div style={{ display: "grid", gap: 16 }}>
          {ACTIONS.map((a) => (
            <Card key={a.key} action={a} />
          ))}
        </div>
      </div>
    </Layout>
  );
}
